import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Link2, RefreshCw, AlertTriangle, ExternalLink } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { formatCurrency } from '../../lib/formatters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UnparseableRefiTransactions() {
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linkOpen, setLinkOpen] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [targetId, setTargetId] = useState('');
  const [linking, setLinking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/refi-linkage/unlinked`, { headers: getAuthHeaders() });
      const d = await r.json();
      setItems(d.items || []);
    } catch (e) {
      toast.error('Failed to load unparseable refis');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const openLink = (item) => {
    setActiveItem(item);
    setTargetId(item.parsed_target || '');
    setLinkOpen(true);
  };

  const submitLink = async () => {
    if (!targetId.trim()) {
      toast.error('Enter a target deal ID');
      return;
    }
    setLinking(true);
    try {
      const r = await fetch(`${API}/admin/refi-linkage/manual-link`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ tx_id: activeItem.tx_id, target_deal_id_number: targetId.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Link failed');
      toast.success(`Linked: ${d.old_deal_id_number} → ${d.new_deal_id_number}`);
      setLinkOpen(false);
      setActiveItem(null);
      setTargetId('');
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLinking(false);
    }
  };

  return (
    <>
      <Card className="border-l-4 border-l-amber-500" data-testid="unparseable-refis-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                Unparseable Refi Transactions
              </CardTitle>
              <CardDescription>
                REFINANCE_PAYOFF transactions whose target deal could not be auto-detected. Manually link each to its successor deal.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-unparseable-btn">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-slate-500 py-6 text-center">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-sm text-emerald-700 bg-emerald-50 rounded-md p-4 text-center" data-testid="unparseable-empty">
              All refi transactions are linked. Nothing to do here.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-slate-200 text-slate-600">
                    <th className="py-2 px-2 font-medium">Old Deal</th>
                    <th className="py-2 px-2 font-medium">Date</th>
                    <th className="py-2 px-2 font-medium text-right">Amount</th>
                    <th className="py-2 px-2 font-medium">Reference / Notes</th>
                    <th className="py-2 px-2 font-medium">Issue</th>
                    <th className="py-2 px-2 font-medium text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.tx_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`unparseable-row-${it.tx_id}`}>
                      <td className="py-2 px-2">
                        <a
                          href={`/deals/${it.old_deal_uuid}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:underline font-medium inline-flex items-center gap-1"
                        >
                          #{it.old_deal_id_number}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                        {it.old_deal_name && (
                          <div className="text-xs text-slate-500 truncate max-w-[18rem]">{it.old_deal_name}</div>
                        )}
                      </td>
                      <td className="py-2 px-2 text-slate-700">{it.date}</td>
                      <td className="py-2 px-2 text-right font-mono">{formatCurrency(it.amount)}</td>
                      <td className="py-2 px-2 text-slate-700 max-w-[20rem]">
                        <div className="font-mono text-xs truncate" title={it.reference}>{it.reference || '—'}</div>
                        {it.notes && <div className="text-xs text-slate-500 truncate" title={it.notes}>{it.notes}</div>}
                      </td>
                      <td className="py-2 px-2">
                        <Badge variant={it.issue === 'no_target_parsed' ? 'destructive' : 'secondary'}>
                          {it.issue === 'no_target_parsed' ? 'No target' : `Target #${it.parsed_target} not found`}
                        </Badge>
                      </td>
                      <td className="py-2 px-2 text-right">
                        <Button size="sm" onClick={() => openLink(it)} data-testid={`manual-link-btn-${it.tx_id}`}>
                          <Link2 className="h-4 w-4 mr-1" />
                          Manually Link
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="sm:max-w-md" data-testid="manual-link-dialog">
          <DialogHeader>
            <DialogTitle>Manually Link Refi Transaction</DialogTitle>
            <DialogDescription>
              Enter the target (successor) deal ID number. This will bidirectionally link the deals and patch this transaction's reference.
            </DialogDescription>
          </DialogHeader>
          {activeItem && (
            <div className="space-y-3 text-sm">
              <div className="bg-slate-50 rounded p-3 space-y-1">
                <div><span className="text-slate-500">Old deal:</span> <span className="font-medium">#{activeItem.old_deal_id_number}</span> — {activeItem.old_deal_name || '—'}</div>
                <div><span className="text-slate-500">Amount:</span> {formatCurrency(activeItem.amount)} on {activeItem.date}</div>
                <div className="font-mono text-xs"><span className="text-slate-500 font-sans">Reference:</span> {activeItem.reference || '—'}</div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" htmlFor="target-deal-id">Target deal ID</label>
                <Input
                  id="target-deal-id"
                  value={targetId}
                  onChange={(e) => setTargetId(e.target.value)}
                  placeholder="e.g. 10757"
                  autoFocus
                  data-testid="manual-link-target-input"
                  onKeyDown={(e) => { if (e.key === 'Enter') submitLink(); }}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkOpen(false)} disabled={linking}>Cancel</Button>
            <Button onClick={submitLink} disabled={linking || !targetId.trim()} data-testid="manual-link-submit-btn">
              <Link2 className="h-4 w-4 mr-2" />
              {linking ? 'Linking…' : 'Link Deals'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
